import type { ApplicationGridProps } from '../types/components'
import type { MouseEvent } from 'react'
import { Link } from 'react-router-dom'
import { User, Smartphone, Check, AlertCircle } from 'lucide-react'
import {
  formatApplicantId,
  formatApplicationId,
  formatPhoneNumber,
  formatPaymentDueDate,
  formatEmail,
  isCertificatePaid,
} from '../utils/applicationFormatters'
import { APPLICATION_STATUS_OPTIONS } from '../utils/applicationConstants'
import { hasPolicy, canUserOpenApplicationDetail } from '../utils/applicationPolicies'
import { useAppSelector } from '@/app/hooks'
import { selectUser } from '@/features/auth/authSlice'
import { Tooltip } from '@/components/ui/Tooltip'
import { SectionInfoIcon } from '@/components/ui/SectionInfoIcon'
import styles from '../styles/applicationList.module.css'


export function ApplicationGrid({
  applications,
  onView,
  onImpersonate,
  onShowDenialReasons,
  onOpenProductDetails,
}: ApplicationGridProps) {
  const user = useAppSelector(selectUser)

  const getStatusLabel = (status: string): string => {
    const statusOption = APPLICATION_STATUS_OPTIONS.find((opt) => opt.value === status)
    return statusOption?.label || status
  }

  const canOpenApplicationDetail = canUserOpenApplicationDetail(user)
  const canViewCreditScore =
    user &&
    hasPolicy(user, ['application_detail_view']) &&
    hasPolicy(user, ['application_list_credit_score_view'])
  const canShowDenialIcon = user ? hasPolicy(user, ['show_denied_reason_icon']) : false
  const canImpersonate = user ? hasPolicy(user, ['user_management_impersonate_applicant']) : false
  const canShowDAFIcon = user ? hasPolicy(user, ['allow_dynamic_access_fee_signal_icon']) : false
  const canShowProduct = user ? hasPolicy(user, ['application_list_product_filter']) : false

  const handleApplicantClick = (
    event: MouseEvent<HTMLAnchorElement>,
    applicantId: number,
  ) => {
    if (!canOpenApplicationDetail) return
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return
    }

    if (onView) {
      event.preventDefault()
      onView(applicantId)
    }
  }

  if (applications.length === 0) {
    return (
      <div
        className="p-8 text-center text-slate-500"
        data-testid="application-list-grid-empty"
      >
        No applications found
      </div>
    )
  }

  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 p-4"
      data-testid="application-list-grid"
    >
      {applications.map((application, index) => {
        const isDenied = application.applicant_status?.toLowerCase() === 'denied'
        const showDAFIcon = Boolean(application.allow_dynamic_access_fee) && canShowDAFIcon
        const cardTestId = `application-list-grid-card-${application.applicant_id}`

        return (
          <div
            key={application.applicant_id}
            className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm hover:shadow-md transition-shadow flex flex-col gap-2"
            data-testid={cardTestId}
          >
            <div
              className="flex items-start justify-between gap-2"
              data-testid={`${cardTestId}-header`}
            >
              {!canOpenApplicationDetail ? (
                <span
                  className="text-left font-semibold text-slate-800"
                  data-testid={`${cardTestId}-applicant-text`}
                >
                  {formatApplicantId(application.applicant_id)}
                </span>
              ) : (
                <Link
                  to={`/application/${application.applicant_id}`}
                  onClick={(event) => handleApplicantClick(event, application.applicant_id)}
                  className={`${styles.link} text-left font-semibold text-blue-600 hover:underline`}
                  data-testid={`${cardTestId}-applicant-link`}
                >
                  {formatApplicantId(application.applicant_id)}
                </Link>
              )}
            </div>

            <div className="space-y-1.5 text-sm" data-testid={`${cardTestId}-body`}>
              <div data-testid={`${cardTestId}-application-field`}>
                <p
                  className="text-xs text-slate-500"
                  data-testid={`${cardTestId}-application-label`}
                >
                  Application
                </p>
                <p
                  className="font-medium text-slate-900"
                  data-testid={`${cardTestId}-application-value`}
                >
                  {formatApplicationId(application.application_id)}
                </p>
              </div>
              <div data-testid={`${cardTestId}-property-field`}>
                <p
                  className="text-xs text-slate-500"
                  data-testid={`${cardTestId}-property-label`}
                >
                  Property
                </p>
                <p
                  className="text-slate-900 truncate"
                  title={application.property_name}
                  data-testid={`${cardTestId}-property-value`}
                >
                  {application.property_name}
                </p>
              </div>
              <div data-testid={`${cardTestId}-resident-field`}>
                <p
                  className="text-xs text-slate-500"
                  data-testid={`${cardTestId}-resident-label`}
                >
                  Resident
                </p>
                <p className="text-slate-900" data-testid={`${cardTestId}-resident-value`}>
                  {application.resident_name}
                </p>
              </div>
              <div data-testid={`${cardTestId}-email-field`}>
                <p className="text-xs text-slate-500" data-testid={`${cardTestId}-email-label`}>
                  Email
                </p>
                <p className="text-slate-900 break-all" data-testid={`${cardTestId}-email-value`}>
                  {formatEmail(application.email)}
                </p>
              </div>
              <div data-testid={`${cardTestId}-phone-field`}>
                <p className="text-xs text-slate-500" data-testid={`${cardTestId}-phone-label`}>
                  Phone
                </p>
                <p className="text-slate-900" data-testid={`${cardTestId}-phone-value`}>
                  {formatPhoneNumber(application.mobile_no)}
                </p>
              </div>
              <div
                className="flex items-center gap-2"
                data-testid={`${cardTestId}-status-field`}
              >
                <p className="text-xs text-slate-500" data-testid={`${cardTestId}-status-label`}>
                  Status
                </p>
                {isDenied && canShowDenialIcon && (
                  <button
                    type="button"
                    onClick={() => onShowDenialReasons?.(application.applicant_id)}
                    className="p-0.5"
                    aria-label="View denial reasons"
                    data-testid={`${cardTestId}-denial-reasons-button`}
                  >
                    <AlertCircle className="h-4 w-4 text-red-500" aria-hidden />
                  </button>
                )}
                <span
                  className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-800"
                  data-testid={`${cardTestId}-status-value`}
                >
                  {getStatusLabel(application.applicant_status)}
                </span>
              </div>
              <div data-testid={`${cardTestId}-progress-field`}>
                <p className="text-xs text-slate-500" data-testid={`${cardTestId}-progress-label`}>
                  Progress
                </p>
                <p className="text-slate-900" data-testid={`${cardTestId}-progress-value`}>
                  {application.progress_status}
                </p>
              </div>
              {canShowProduct && (
                <div data-testid={`${cardTestId}-product-field`}>
                  <p className="text-xs text-slate-500" data-testid={`${cardTestId}-product-label`}>
                    Product
                  </p>
                  <div className="flex items-center gap-2 min-w-0">
                    <p
                      className="text-slate-900 truncate min-w-0 flex-1"
                      title={application.product_name ?? undefined}
                      data-testid={`${cardTestId}-product-value`}
                    >
                      {application.product_name || '—'}
                    </p>
                    {showDAFIcon && (
                      <SectionInfoIcon
                        align="start"
                        maxWidth={360}
                        iconSize={16}
                        isLastRow={
                          index >= Math.max(0, applications.length - 3)
                        }
                        aria-label="Product details"
                        data-testid={`application-list-grid-product-popover-${application.applicant_id}`}
                        onClick={() => onOpenProductDetails?.(application)}
                        clampPanelHeight
                        contentClassName="px-3 py-2 text-xs text-slate-700"
                        content={
                          <>
                            <p className="mb-1.5 font-semibold text-slate-800">
                              {application.product_name || 'Product'}
                            </p>
                            {application.product_description?.trim() ? (
                              <p className="whitespace-pre-wrap">
                                {application.product_description}
                              </p>
                            ) : (
                              <p className="whitespace-pre-wrap">No description.</p>
                            )}
                          </>
                        }
                      />
                    )}
                  </div>
                </div>
              )}
              {application.payment_due_date && (
                <div data-testid={`${cardTestId}-payment-due-field`}>
                  <p
                    className="text-xs text-slate-500"
                    data-testid={`${cardTestId}-payment-due-label`}
                  >
                    Payment Due
                  </p>
                  <p className="text-slate-900" data-testid={`${cardTestId}-payment-due-value`}>
                    {formatPaymentDueDate(application.payment_due_date)}
                  </p>
                </div>
              )}
              {canViewCreditScore && application.corelogic_applicant_score && (
                <div data-testid={`${cardTestId}-credit-score-field`}>
                  <p
                    className="text-xs text-slate-500"
                    data-testid={`${cardTestId}-credit-score-label`}
                  >
                    Credit Score
                  </p>
                  <p className="text-slate-900" data-testid={`${cardTestId}-credit-score-value`}>
                    {application.corelogic_applicant_score}
                  </p>
                </div>
              )}
              {canViewCreditScore &&
                application.income_score !== null &&
                application.income_score !== undefined &&
                application.income_score !== '' && (
                <div data-testid={`${cardTestId}-income-score-field`}>
                  <p
                    className="text-xs text-slate-500"
                    data-testid={`${cardTestId}-income-score-label`}
                  >
                    Income Score
                  </p>
                  <p className="text-slate-900" data-testid={`${cardTestId}-income-score-value`}>
                    {application.income_score}
                  </p>
                </div>
              )}
              {isCertificatePaid(application.certificate_paid) && (
                <div
                  className="flex items-center gap-1"
                  data-testid={`${cardTestId}-contract-paid-field`}
                >
                  <Check
                    className="h-4 w-4 text-green-600"
                    data-testid={`${cardTestId}-contract-paid-icon`}
                    aria-hidden
                  />
                  <span
                    className="text-xs text-slate-600"
                    data-testid={`${cardTestId}-contract-paid-label`}
                  >
                    Contract Paid
                  </span>
                </div>
              )}
            </div>

            {canImpersonate && application.role_id && (
              <div
                className="flex flex-wrap gap-2 pt-2 mt-auto border-t border-slate-100"
                data-testid={`${cardTestId}-actions`}
              >
                <Tooltip content="Impersonate">
                  <button
                    type="button"
                    className={`${styles.iconButton} flex-1 flex items-center justify-center gap-1`}
                    onClick={() => onImpersonate?.(application.role_id!, application.applicant_id)}
                    aria-label="Impersonate"
                    data-testid={`${cardTestId}-impersonate-button`}
                  >
                    <User className="h-4 w-4" aria-hidden />
                    <span className="text-xs">View</span>
                  </button>
                </Tooltip>
                <Tooltip content="Impersonate Mobile">
                  <button
                    type="button"
                    className={`${styles.iconButton} flex-1 flex items-center justify-center gap-1`}
                    onClick={() => onImpersonate?.(application.role_id!, application.applicant_id, true)}
                    aria-label="Impersonate Mobile"
                    data-testid={`${cardTestId}-impersonate-mobile-button`}
                  >
                    <Smartphone className="h-4 w-4" aria-hidden />
                    <span className="text-xs">Mobile</span>
                  </button>
                </Tooltip>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
